import React, { Fragment, useState, useCallback } from 'react';
import {
  TextField,
  ReferenceField,
  ReferenceManyField,
  useTranslate,
  ChipField,
} from 'react-admin';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {
  NoPermissions,
  ShowController,
  KycLevelField,
  EmptyResultsDatagrid,
} from '~/components/ra';
import RaPagination from '~/components/Pagination/RaPagination';
import { Longdash } from '~/utils';
import { isEmpty } from 'lodash';
import { PlusField, AutocompleteArrayTextInput, SelectInput } from '~/components';
import { PIAvailabilityShow, PIAvailabilityCreate } from '../PIAvailability';
import usePath from '~/hooks/usePath';
import { debounce, set as lodashSet } from 'lodash';
import { useGetAllProfileTags } from '~/hooks/profileTags';
import { Skeleton } from '@material-ui/lab';
import { Solution } from '~/types';
import { useExternalSourceTypes } from '~/hooks/externalSources';
import { useProductsBySolutionId } from '~/hooks/product';
import { rowsPerPageOptionsDefault, perPageDefault } from '~/utils/pagination';

interface PIAvailabilityTabProps {
  record: Solution;
  permissions: any;
}

const PIAvailabilityTab = (props: PIAvailabilityTabProps) => {
  const { permissions, record: solutionRecord, ...rest } = props;
  const classes = useStyles();
  const [filters, setFilters] = useState<any>({});
  const [showData, setShowData] = useState(null);
  const handleShowOpen = (data: any) => {
    permissions.includes('solution.paymentInstrumentAvailabilityRules.view') && setShowData(data);
  };
  const handleShowClose = () => setShowData(null);

  if (!permissions) return null;
  if (!permissions.includes('solution.paymentInstrumentAvailabilityRules.list'))
    return <NoPermissions />;
  return (
    <Fragment>
      <PIAvailabilityShow
        record={showData}
        solutionRecord={solutionRecord}
        onClose={handleShowClose}
        permissions={permissions}
        {...rest}
      />
      <Grid container justify='space-between' alignItems='center' className={classes.spacings}>
        <Grid item>
          <Filters
            {...props}
            filters={filters}
            setFilters={setFilters}
            solutionId={solutionRecord?.id}
          />
        </Grid>
        <Grid item>
          <PIAvailabilityCreate
            record={showData}
            permissions={permissions}
            solutionRecord={solutionRecord}
          />
        </Grid>
      </Grid>
      <ReferenceManyField
        label='Configurations'
        reference='paymentInstrumentAvailabilityRules'
        target='solutions'
        pagination={
          <RaPagination
            defaultPerPage={perPageDefault}
            rowsPerPageOptions={rowsPerPageOptionsDefault}
          />
        }
        filter={filters}
        perPage={perPageDefault}
        {...props}
        record={solutionRecord}
      >
        <PIAvailabilityDatagrid
          rowClick={(_id: any, _basePath: any, record: any) => {
            handleShowOpen(record);
          }}
        />
      </ReferenceManyField>
    </Fragment>
  );
};

const Filters = (props: any) => {
  const { filters, setFilters, solutionId } = props;

  const translate = useTranslate();
  const t = (key: string) =>
    translate(`resources.solutions.paymentInstrumentAvailabilityRules.${key}`);

  const { changePath } = usePath();
  const debouncedSetFilters = useCallback(debounce(setFilters, 400), [setFilters]);
  const handleChange = (e: any) => {
    if (!e?.target) return null;
    const { name, value } = e.target;
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };
  const handleArrayChange = (name: string) => (_event: any, value: any) => {
    changePath({ page: 1 });
    debouncedSetFilters((prev: any) => {
      return { ...lodashSet(prev, name, value) };
    });
  };

  const { data: externalSourceTypesData, isLoading } = useExternalSourceTypes();
  const externalSourceTypes = isLoading
    ? []
    : externalSourceTypesData?.map((type) => ({ value: type, text: type }));
  const { data: productsData, isLoading: productsLoading } = useProductsBySolutionId(solutionId);
  const products = productsLoading
    ? []
    : productsData?.map((product) => ({
        value: product.id,
        text: product.name,
      }));
  const { data: allTags, isLoading: allTagsLoading } = useGetAllProfileTags();
  const autocompleteOptions = allTags?.map((tag) => tag?.name);

  const classes = useStyles();
  return (
    <form className={classes.filters}>
      <SelectInput
        allowEmpty
        name='productId'
        source='productId'
        label={t('product')}
        value={filters.productId ?? ''}
        options={products as any}
        emptyText={t('any')}
        onChange={handleChange}
        className={classes.formControl}
        disableHelperText
      />
      <SelectInput
        allowEmpty
        name='externalSourceType'
        source='externalSourceType'
        label={t('externalSourceType')}
        value={filters.externalSourceType ?? ''}
        options={externalSourceTypes as any}
        emptyText={t('any')}
        onChange={handleChange}
        className={classes.formControl}
      />
      {allTagsLoading ? (
        <Skeleton width='100%' />
      ) : (
        <AutocompleteArrayTextInput
          name='allowedProfileTags'
          source='allowedProfileTags'
          label={t('allowedProfileTags')}
          options={autocompleteOptions}
          onChange={handleArrayChange('allowedProfileTags')}
          value={filters?.allowedProfileTags}
          className={classes.formControlLong}
          freeSolo={false}
          disableHelperText
        />
      )}
      {allTagsLoading ? (
        <Skeleton width='100%' />
      ) : (
        <AutocompleteArrayTextInput
          name='disallowedProfileTags'
          source='disallowedProfileTags'
          label={t('disallowedProfileTags')}
          options={autocompleteOptions}
          onChange={handleArrayChange('disallowedProfileTags')}
          value={filters?.disallowedProfileTags}
          className={classes.formControlLong}
          freeSolo={false}
          disableHelperText
        />
      )}
    </form>
  );
};

const PIAvailabilityDatagrid = (props: any) => {
  const { rowClick, ...rest } = props;
  const translate = useTranslate();
  const t = (key: string) =>
    translate(`resources.solutions.paymentInstrumentAvailabilityRules.${key}`);
  return (
    <EmptyResultsDatagrid
      emptyText={translate('resources.solutions.fields.noResultsFound')}
      rowClick={rowClick}
      {...rest}
    >
      <ShowController label={t('paymentInstrument')}>
        {({ showRecord }) => {
          if (!showRecord) return Longdash;
          if (showRecord.productId) {
            return (
              <ReferenceField source='productId' reference='products' link={false}>
                <TextField source='name' allowEmpty emptyText={Longdash} />
              </ReferenceField>
            );
          } else if (showRecord.externalSourceType) {
            return <TextField source='externalSourceType' allowEmpty emptyText={Longdash} />;
          } else return Longdash;
        }}
      </ShowController>
      <KycLevelField source='kycLevel' label={t('profileKycLevel')} sortable={false} />
      <ShowController label={t('allowedProfileTags')} sortable={false}>
        {({ showRecord }) => {
          if (isEmpty(showRecord?.allowedProfileTags)) return Longdash;
          return (
            <PlusField
              items={showRecord?.allowedProfileTags}
              mainComponent={(tag) => <ChipField record={{ name: tag }} source='name' key={tag} />}
              tooltipsComponent={(tag) => (
                <ChipField record={{ name: tag }} source='name' key={tag} />
              )}
            />
          );
        }}
      </ShowController>
      <ShowController label={t('disallowedProfileTags')} sortable={false}>
        {({ showRecord }) => {
          if (isEmpty(showRecord?.disallowedProfileTags)) return Longdash;
          return (
            <PlusField
              items={showRecord?.disallowedProfileTags}
              mainComponent={(tag) => <ChipField record={{ name: tag }} source='name' key={tag} />}
              tooltipsComponent={(tag) => (
                <ChipField record={{ name: tag }} source='name' key={tag} />
              )}
            />
          );
        }}
      </ShowController>
    </EmptyResultsDatagrid>
  );
};

const useStyles = makeStyles((theme) => ({
  spacings: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filters: {
    alignItems: 'flex-start',
    display: 'flex',
    justifyContent: 'space-between',
  },
  formControl: {
    marginRight: theme.spacing(1),
    minWidth: 150,
    maxWidth: 270,
    '&:last-child': {
      marginRight: 0,
    },
  },
  formControlLong: {
    marginRight: theme.spacing(1),
    minWidth: 300,
    maxWidth: 300,
    '&:last-child': {
      marginRight: 0,
    },
  },
}));

export default PIAvailabilityTab;
